/**
 * Cliente API Gmail para el panel de admin.
 * Todas las llamadas usan credentials: 'include' para la sesión.
 */

function getBaseUrl(): string {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  return '/api';
}

export interface GmailStatus {
  connected: boolean;
  email: string | null;
}

export async function getGmailStatus(): Promise<{ success: boolean } & GmailStatus> {
  const res = await fetch(`${getBaseUrl()}/gmail/status`, { credentials: 'include' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error al obtener estado de Gmail');
  return data;
}

export interface GmailLabel {
  id: string;
  name: string;
  type: string;
  labelListVisibility?: string;
  messagesTotal?: number;
  messagesUnread?: number;
  threadsTotal?: number;
  threadsUnread?: number;
}

export async function getGmailLabels(): Promise<{ success: boolean; labels: GmailLabel[] }> {
  const res = await fetch(`${getBaseUrl()}/gmail/labels`, { credentials: 'include' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error al listar etiquetas');
  return data;
}

export interface GmailMessageListItem {
  id: string;
  threadId?: string;
  labelIds?: string[];
  snippet?: string;
  internalDate?: string; // milliseconds as string (Gmail API)
  payload?: {
    headers?: { name: string; value: string }[];
  };
}

export interface GmailListResponse {
  success: boolean;
  messages: GmailMessageListItem[];
  nextPageToken: string | null;
  resultSizeEstimate: number;
}

export async function getGmailMessages(params?: {
  maxResults?: number;
  pageToken?: string;
  q?: string;
  labelId?: string;
}): Promise<GmailListResponse> {
  const sp = new URLSearchParams();
  if (params?.maxResults) sp.set('maxResults', String(params.maxResults));
  if (params?.pageToken) sp.set('pageToken', params.pageToken);
  if (params?.q) sp.set('q', params.q);
  if (params?.labelId) sp.set('labelId', params.labelId);
  const qs = sp.toString();
  const url = `${getBaseUrl()}/gmail/messages${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error al listar mensajes');
  return data;
}

export interface GmailMessageFull {
  id: string;
  threadId?: string;
  labelIds?: string[];
  snippet?: string;
  internalDate?: string;
  payload?: {
    headers?: { name: string; value: string }[];
    body?: { data?: string };
    parts?: { mimeType?: string; body?: { data?: string }; filename?: string }[];
  };
}

export async function getGmailMessage(id: string): Promise<{ success: boolean; message: GmailMessageFull }> {
  const res = await fetch(`${getBaseUrl()}/gmail/messages/${encodeURIComponent(id)}`, { credentials: 'include' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error al obtener mensaje');
  return data;
}

/** Marca un mensaje como leído (quita la etiqueta UNREAD en Gmail). */
export async function markGmailMessageAsRead(id: string): Promise<{ success: boolean }> {
  const res = await fetch(`${getBaseUrl()}/gmail/messages/${encodeURIComponent(id)}/read`, {
    method: 'POST',
    credentials: 'include',
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error al marcar como leído');
  return data;
}

export async function sendGmailMessage(body: {
  to: string;
  subject: string;
  body?: string;
  sendAs?: string;
}): Promise<{ success: boolean; message: { id?: string } }> {
  const res = await fetch(`${getBaseUrl()}/gmail/messages/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error al enviar');
  return data;
}

export interface SendAsItem {
  sendAsEmail: string;
  displayName: string;
  isPrimary: boolean;
}

export async function getGmailSendAs(): Promise<{ success: boolean; sendAs: SendAsItem[] }> {
  const res = await fetch(`${getBaseUrl()}/gmail/sendAs`, { credentials: 'include' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error al listar identidades');
  return data;
}

/** URL para iniciar OAuth (el usuario debe ser redirigido aquí con la sesión de admin). */
export function getGmailOAuthUrl(): string {
  return `${getBaseUrl()}/gmail/oauth`;
}

export async function exportGmailAddressesToExcel(body: {
  dateFrom: string;
  dateTo: string;
  allFolders: boolean;
  labelIds?: string[];
  maxMessages?: number;
  extraQuery?: string;
}): Promise<Blob> {
  const res = await fetch(`${getBaseUrl()}/gmail/export-addresses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || 'Error al exportar direcciones');
  }
  return res.blob();
}
